<template>
  <Dialog
    :visible="visible"
    modal
    :closable="!submitting"
    :show-header="false"
    :style="{ width: '760px', maxWidth: '95vw' }"
    :content-style="{ padding: 0, borderRadius: '16px' }"
    :pt="{ root: { class: 'repay-invoice-root' } }"
    @update:visible="onVisibleChange"
  >
    <header class="repay-invoice__header">
      <div class="repay-invoice__header-text">
        <h2 class="repay-invoice__title">
          {{ t('billing.repayInvoice.title') }}
        </h2>
        <p
          v-if="invoice"
          class="repay-invoice__subtitle"
        >
          {{ t('billing.repayInvoice.subtitle', { date: formattedDate }) }}
        </p>
      </div>
      <button
        type="button"
        class="repay-invoice__close"
        :disabled="submitting"
        @click="onClose"
      >
        <i class="fa-regular fa-xmark" />
      </button>
    </header>

    <div
      v-if="invoice"
      class="repay-invoice__body"
    >
      <ReviewAndPayCard
        v-model:payment-mode="paymentMode"
        v-model:payment-method-id="paymentMethodId"
        :recap-title="invoice.description || t('billing.repayInvoice.title')"
        :recap-subtitle="t('billing.repayInvoice.invoiceMeta', { id: invoice.id })"
        recap-icon="fa-regular fa-receipt"
        :recap-lines="recapLines"
        :total-label="t('billing.repayInvoice.totalToPay')"
        :total-value="invoice.amountTtc.label"
        :default-card="defaultCard"
        :all-cards="allCards"
        :amount="invoice.amountTtc.amount"
        :currency="invoice.amountTtc.currency"
        :allowed-methods="ALLOWED_METHODS_ONESHOT"
        :dropin-submit-handler="onDropinSubmit"
        :dropin-details-handler="onDropinDetails"
        :payment-error="paymentError"
        :disabled="submitting"
        :cta-label="hasDefaultCard ? t('billing.repayInvoice.payCta', { amount: invoice.amountTtc.label }) : ''"
        cta-icon="fa-regular fa-lock"
        :cta-submitting="submitting"
        :trust-text="t('billing.review.trustSecure')"
        @dropin-success="onDropinSuccess"
        @dropin-error="onDropinError"
        @cta-click="onPayWithSavedCard"
      />

      <div class="repay-invoice__foot">
        <button
          type="button"
          class="repay-invoice__btn-cancel"
          :disabled="submitting"
          @click="onClose"
        >
          {{ t('billing.repayInvoice.cancel') }}
        </button>
      </div>
    </div>
  </Dialog>
</template>

<script lang="ts">
import {
  computed, defineComponent, PropType, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';
import moment from 'moment';
import Dialog from 'primevue/dialog';
import ReviewAndPayCard, { ReviewAndPayLine } from '@/components/billing/shared/ReviewAndPayCard.vue';
import {
  ActionResult,
  ALLOWED_METHODS_ONESHOT,
  confirmPlan,
  repayInvoice,
} from '@/composables/billing/billingApi';
import { useStructuralReload } from '@/composables/billing/useStructuralReload';
import { InvoiceListItem, PaymentMethodItem } from '@/types/billing-types';

type PaymentMode = 'existing' | 'new';

export default defineComponent({
  name: 'RepayInvoiceDialog',

  components: { Dialog, ReviewAndPayCard },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },

    invoice: {
      type: Object as PropType<InvoiceListItem | null>,
      default: null,
    },
  },

  emits: ['update:visible', 'paid'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();
    const handleReload = useStructuralReload();

    const submitting = ref(false);
    const paymentError = ref<string | null>(null);
    const paymentMode = ref<PaymentMode>('existing');
    const paymentMethodId = ref<string | null>(null);

    const overview = computed(() => store.state.billing.overview);
    const allCards = computed<PaymentMethodItem[]>(
      () => overview.value?.paymentMethods?.items ?? [],
    );
    const defaultCard = computed(() => (
      allCards.value.find((p: any) => p.isDefault) ?? null
    ));
    const hasDefaultCard = computed(() => defaultCard.value !== null);

    const formattedDate = computed(() => (
      props.invoice ? moment(props.invoice.date).format('D MMMM YYYY') : ''
    ));

    // Single « Montant HT » line — visually mirrors the WalletTopUpReview recap
    // where a short breakdown precedes the « Total à payer » box.
    const recapLines = computed<ReviewAndPayLine[]>(() => {
      if (!props.invoice) return [];
      return [
        {
          label: t('billing.repayInvoice.amountHtLine'),
          value: props.invoice.amountHt.label,
        },
      ];
    });

    const reset = () => {
      paymentError.value = null;
      paymentMode.value = hasDefaultCard.value ? 'existing' : 'new';
      paymentMethodId.value = null;
    };

    const onClose = () => {
      if (submitting.value) return;
      emit('update:visible', false);
      setTimeout(reset, 220);
    };

    const onVisibleChange = (v: boolean) => { if (!v) onClose(); };

    const onPayWithSavedCard = async () => {
      if (!props.invoice) return;
      paymentError.value = null;
      submitting.value = true;
      try {
        const result = await repayInvoice(props.invoice.id);
        await handleReload(result, {
          onFailure: (err) => {
            paymentError.value = err;
          },
          onActionRequired: () => {
            paymentError.value = t('billing.payment.actionRequired');
          },
        });
        if (result.status === 'completed') {
          emit('paid', props.invoice.id);
          emit('update:visible', false);
        }
      } finally {
        submitting.value = false;
      }
    };

    // ─── Drop-in handlers (new-card path) ────────────────────────────────
    const onDropinSubmit = (
      adyenStateData: Record<string, any>,
    ): Promise<ActionResult> => {
      if (!props.invoice) {
        return Promise.resolve({
          status: 'failed',
          error: t('billing.repayInvoice.missingInvoice'),
        });
      }
      return repayInvoice(props.invoice.id, adyenStateData);
    };

    const onDropinDetails = (
      adyenStateData: Record<string, any>,
    ): Promise<ActionResult> => confirmPlan({ adyenStateData });

    const onDropinSuccess = async () => {
      await store.dispatch('billing/refetchOverview');
      if (props.invoice) emit('paid', props.invoice.id);
      emit('update:visible', false);
    };

    const onDropinError = (err: string) => {
      paymentError.value = err;
    };

    watch(() => props.visible, (v) => { if (v) reset(); });
    watch(hasDefaultCard, (has) => {
      paymentMode.value = has ? 'existing' : 'new';
    });

    return {
      t,
      ALLOWED_METHODS_ONESHOT,
      submitting,
      paymentError,
      paymentMode,
      paymentMethodId,
      defaultCard,
      allCards,
      hasDefaultCard,
      formattedDate,
      recapLines,
      onVisibleChange,
      onClose,
      onPayWithSavedCard,
      onDropinSubmit,
      onDropinDetails,
      onDropinSuccess,
      onDropinError,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.repay-invoice {
  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 24px 28px 16px;
    gap: 24px;
  }
  &__header-text { min-width: 0; }
  &__title {
    font-size: 1.375rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    letter-spacing: -0.02em;
  }
  &__subtitle {
    font-size: 0.875rem;
    color: #64748b;
    margin: 4px 0 0;
    line-height: 1.5;
  }
  &__close {
    border: none;
    background: transparent;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    cursor: pointer;
    transition: all 160ms ease;
    flex-shrink: 0;

    &:hover:not(:disabled) {
      background: #f1f5f9;
      color: #0f172a;
    }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
    i { font-size: 1.125rem; }
  }

  &__body {
    padding: 0 28px 24px;
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  // ─── Footer buttons ──────────────────────────────────
  &__foot {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 16px;
    border-top: 1px solid #f1f5f9;
  }
  &__btn-cancel {
    appearance: none;
    background: transparent;
    border: none;
    padding: 10px 16px;
    font-size: 0.875rem;
    color: #64748b;
    font-weight: 600;
    cursor: pointer;
    border-radius: 100px;
    transition: all 160ms ease;

    &:hover:not(:disabled) {
      background: #f1f5f9;
      color: #0f172a;
    }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }
}
</style>

<style lang="scss">
.repay-invoice-root.p-dialog {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 24px 80px -24px rgba(15, 23, 42, 0.32);
  max-height: 92vh;
  display: flex;
  flex-direction: column;

  .p-dialog-content {
    padding: 0 !important;
    border-radius: 0;
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
  }
}
</style>
